import moment from "moment-timezone";

import { parseTimeInput, capitalizeFirstLetter } from "../../utils/payRequestConstants";

/*
 * Every reason the pay request form cannot be submitted, ordered the way the
 * banner should announce them.
 *
 * Deliberately pure and derived. The form previously wrote a single shared
 * `validationError` string from three unrelated places, so each condition
 * overwrote the others and unrelated actions cleared it. Deriving instead
 * makes over-allocation a property of `inputValues`, which means it is caught
 * whether the time was typed on the calendar cell or in the rate table.
 *
 * Attestation is not a rule here: like mobile, it gates the button silently.
 */

const dateLabel = (date) => moment(date, "YYYY-MM-DD").format("MMM D");

export const getPayRequestErrors = ({
  isEdit = false,
  location,
  payRequestType,
  payRequestName,
  shiftType,
  selectedDates = [],
  inputValues,
  rows = [],
  description = "",
  getAvailableMinutesForDate,
  isCalculating = false,
  isAmountContract = false,
  isShiftContract = false,
  thresholdUnitType = "",
}) => {
  const errors = [];

  /*
   * The contractual selections and the date are read-only once editing, so
   * they cannot be wrong and must never block the save.
   */
  if (!isEdit) {
    if (!location) {
      errors.push({ code: "location", field: "location", message: "Select Location" });
    }
    if (!payRequestType) {
      errors.push({
        code: "payRequestType",
        field: "payRequestType",
        message: "Select Pay Request Type",
      });
    }
    if (!payRequestName) {
      errors.push({
        code: "payRequestName",
        field: "payRequestName",
        message: "Select Pay Request Name",
      });
    }
    if (payRequestName?.show_shift_type_picker && !shiftType) {
      errors.push({ code: "shiftType", field: "shiftType", message: "Select Shift Type" });
    }
    if (selectedDates.length === 0) {
      errors.push({
        code: "dates",
        field: "dates",
        message: "Please select atleast one date and time.",
      });
    }
  }

  // A shift-threshold contract is entered as a decimal quantity, not an
  // HH:MM time, so it gets its own zero-check and skips the hours/minutes
  // availability check below entirely.
  const timeByDate = selectedDates.map((date) => {
    const raw = String(inputValues?.get(date) ?? "").trim();
    if (isShiftContract) return { date, raw, shiftValue: Number(raw) || 0 };
    return { date, raw, ...parseTimeInput(raw) };
  });

  timeByDate
    .filter((entry) => (isShiftContract ? entry.shiftValue === 0 : entry.hours === 0 && entry.minutes === 0))
    .forEach((entry) =>
      errors.push({
        code: "timeZero",
        field: "times",
        message: isShiftContract
          ? `${capitalizeFirstLetter(thresholdUnitType)} must be greater than 0 for ${dateLabel(entry.date)}.`
          : `Hours and minutes cannot both be 0 for ${dateLabel(entry.date)}.`,
      })
    );

  if (!isShiftContract && typeof getAvailableMinutesForDate === "function") {
    timeByDate
      .filter((entry) => entry.raw !== "")
      .forEach((entry) => {
        const available = getAvailableMinutesForDate(entry.date);

        /*
         * A zero budget on a date the availability API has not answered for is
         * not evidence of over-allocation — only editing, where the task's own
         * minutes are added back, can legitimately see a zero remainder.
         */
        if (available === 0 && !isEdit) return;

        if (entry.hours * 60 + entry.minutes > available) {
          errors.push({
            code: "timeOverAllocated",
            field: "times",
            message: `Maximum available hours for ${entry.date} is ${Math.floor(
              available / 60
            )}h ${available % 60}m`,
          });
        }
      });
  }

  /*
   * Cap flags are only meaningful once the debounced calculation has settled,
   * and amount contracts are priced server-side so no row ever carries one.
   */
  // if (!isCalculating && !isAmountContract) {
  //   const flagged = rows.some((row) => row.softFlag || row.hardFlag);
  //   if (flagged && String(description ?? "").trim() === "") {
  //     errors.push({
  //       code: "description",
  //       field: "description",
  //       message: "Please enter a description",
  //     });
  //   }
  // }

  return errors;
};

export default getPayRequestErrors;
